/**
 * API Client for authenticated requests to backend
 * Handles Clerk JWT token injection automatically
 */

import {
  dispatchSessionExpired,
  inferSessionExpiredReason,
  isSessionAuthFailure,
} from "../lib/sessionExpired";
import { shouldRedirect402ToPaywall } from "../utils/billingAllowlist";
import { redirectToBillingPaywall } from "../utils/billingPaywall";
import { handleRateLimitedResponse } from "../utils/httpRateLimit";

// Backend API base URL - loaded from environment variable
// For production: Set REACT_APP_BACKEND_API_URL in .env file
// For development: default to relative `/api` so CRA proxy works.
export const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || "/api";

/**
 * Get Clerk JWT token
 * Uses window.Clerk which is available when ClerkProvider is initialized
 */
export const getClerkToken = async (): Promise<string | null> => {
  if (typeof window === "undefined") return null;
  const clerk = (window as any).Clerk;
  if (!clerk) return null;

  try {
    // In some boot sequences (React 18 + Clerk), `window.Clerk` exists before `session` is ready.
    // Try to wait briefly for Clerk to finish loading before giving up.
    if (!clerk.session && typeof clerk.load === "function") {
      await Promise.race([
        clerk.load(),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Clerk load timeout")), 1500)),
      ]).catch(() => null);
    }

    if (!clerk.session) return null;
    return await clerk.session.getToken();
  } catch (error) {
    console.error("Failed to get Clerk token:", error);
    return null;
  }
};

/**
 * Create an authenticated API client
 * Usage: const apiClient = createApiClient();
 *        const response = await apiClient.fetch('/broker/connect', { method: 'POST' });
 */
export const createApiClient = () => {
  return {
    async fetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
      const token = await getClerkToken();
      
      if (!token) {
        if (typeof window !== "undefined" && (window as any).Clerk?.user) {
          dispatchSessionExpired();
        }
        throw new Error("Authentication required. Please sign in.");
      }

      const response = await fetch(`${BACKEND_API_URL}${endpoint}`, {
        ...options,
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          ...options.headers,
        },
      });
      handleRateLimitedResponse(response, endpoint);
      if (await isSessionAuthFailure(response)) {
        dispatchSessionExpired({ reason: inferSessionExpiredReason() });
      }
      if (response.status === 402 && shouldRedirect402ToPaywall(endpoint)) {
        redirectToBillingPaywall();
      }
      return response;
    },
  };
};

/**
 * Helper function for authenticated fetch requests
 * Usage: const response = await authenticatedFetch('/broker/connect', { method: 'POST' });
 */
export type AuthenticatedFetchInit = RequestInit & {
  /** Broker credential checks can return 401 for invalid API keys — not an app session expiry. */
  skipSessionExpiredOn401?: boolean;
};

export const authenticatedFetch = async (
  endpoint: string,
  options: AuthenticatedFetchInit = {}
): Promise<Response> => {
  const { skipSessionExpiredOn401 = false, ...fetchOptions } = options;
  // Try once; if Clerk isn't ready yet, wait briefly and retry.
  let token = await getClerkToken();
  if (!token && typeof window !== "undefined" && (window as any).Clerk?.load) {
    try {
      await Promise.race([
        (window as any).Clerk.load(),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Clerk load timeout")), 1500)),
      ]).catch(() => null);
    } catch {
      // ignore
    }
    token = await getClerkToken();
  }

  if (!token) {
    if (typeof window !== "undefined" && (window as any).Clerk?.user) {
      dispatchSessionExpired();
    }
    throw new Error("Authentication required. Please sign in.");
  }

  let response: Response;
  try {
    response = await fetch(`${BACKEND_API_URL}${endpoint}`, {
      ...fetchOptions,
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        ...fetchOptions.headers,
      },
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (/failed to fetch|networkerror|load failed|network request failed/i.test(message)) {
      throw new Error(
        "Couldn't reach Tessera. Check your internet connection and try again."
      );
    }
    throw err;
  }
  handleRateLimitedResponse(response, endpoint);
  if (!skipSessionExpiredOn401 && (await isSessionAuthFailure(response))) {
    dispatchSessionExpired({ reason: inferSessionExpiredReason() });
  }
  if (response.status === 402 && shouldRedirect402ToPaywall(endpoint)) {
    redirectToBillingPaywall();
  }
  return response;
};
